import React, {Component} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {FaIcon} from '../../FaIcon';
import {withTranslation} from 'react-i18next';
import './BlockEditorModal.scss';


const BLOCK_TYPES = ['kpi_hero', 'metric_card', 'line_chart', 'bar_chart', 'table', 'text', 'separator'];

const BLOCK_TYPE_ICONS = {
    kpi_hero: 'gauge-high',
    metric_card: 'chart-simple',
    line_chart: 'chart-line',
    bar_chart: 'chart-bar',
    table: 'table',
    text: 'pen-to-square',
    separator: 'grip-lines',
};

const DEFAULT_COL_SPANS = {
    kpi_hero: 3, metric_card: 6, line_chart: 12,
    bar_chart: 6, table: 12, text: 12, separator: 12,
};

const DATE_RANGE_PRESETS = ['THIS_WEEK', 'PREVIOUS_WEEK', 'THIS_MONTH', 'PREVIOUS_MONTH', 'LAST_7_DAYS', 'LAST_30_DAYS'];
const COMPARISON_PRESETS = ['PREVIOUS_PERIOD', 'PREVIOUS_WEEK', 'PREVIOUS_MONTH'];
const DATA_SOURCE_TYPES = ['AD_ACCOUNTS', 'BUSINESS_UNITS'];

const DATA_BLOCK_TYPES = ['kpi_hero', 'metric_card', 'line_chart', 'bar_chart', 'table'];


class BlockEditorModalClass extends Component {
    constructor(props) {
        super(props);
        this.state = this.getInitialState(props);

        this.handleTypeChange = this.handleTypeChange.bind(this);
        this.handleAddField = this.handleAddField.bind(this);
        this.handleRemoveField = this.handleRemoveField.bind(this);
        this.handleFieldKeyDown = this.handleFieldKeyDown.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
    }

    getInitialState(props) {
        const block = props.block;
        if (block) {
            return {
                title: block.title || '',
                type: block.type || 'kpi_hero',
                col_span: block.col_span || DEFAULT_COL_SPANS[block.type] || 12,
                fields: block.query_params?.fields ? [...block.query_params.fields] : [],
                date_range_preset: block.query_params?.date_range_preset || 'THIS_WEEK',
                comparison_range_preset: block.query_params?.comparison_range_preset || 'PREVIOUS_PERIOD',
                data_source_type: block.query_params?.data_source_type || 'AD_ACCOUNTS',
                fieldInput: '',
            };
        }
        return {
            title: '',
            type: 'kpi_hero',
            col_span: DEFAULT_COL_SPANS['kpi_hero'],
            fields: [],
            date_range_preset: 'THIS_WEEK',
            comparison_range_preset: 'PREVIOUS_PERIOD',
            data_source_type: 'AD_ACCOUNTS',
            fieldInput: '',
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.setState(this.getInitialState(this.props));
        }
    }

    handleTypeChange(newType) {
        this.setState({
            type: newType,
            col_span: DEFAULT_COL_SPANS[newType] || 12,
        });
    }

    handleAddField() {
        const field = this.state.fieldInput.trim();
        if (field && !this.state.fields.includes(field)) {
            this.setState(prev => ({
                fields: [...prev.fields, field],
                fieldInput: '',
            }));
        }
    }

    handleRemoveField(index) {
        this.setState(prev => ({
            fields: prev.fields.filter((_, i) => i !== index),
        }));
    }

    handleFieldKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleAddField();
        }
    }

    handleSave() {
        const {type, title, col_span, fields, date_range_preset, comparison_range_preset, data_source_type} = this.state;
        const isDataBlock = DATA_BLOCK_TYPES.includes(type);

        const block = {
            type,
            title: title || undefined,
            col_span: parseInt(col_span, 10) || DEFAULT_COL_SPANS[type] || 12,
        };

        if (isDataBlock) {
            block.query_params = {
                fields: [...fields],
                date_range_preset,
                comparison_range_preset,
                data_source_type,
            };
        }

        if (this.props.block) {
            block.id = this.props.block.id;
        }

        if (this.props.onSave) {
            this.props.onSave(block);
        }
    }

    handleDelete() {
        if (window.confirm(this.props.t('templates.block_delete_confirm'))) {
            if (this.props.onDelete) {
                this.props.onDelete();
            }
        }
    }

    render() {
        const {t, isOpen, toggle, block} = this.props;
        const {type, title, col_span, fields, fieldInput, date_range_preset, comparison_range_preset, data_source_type} = this.state;
        const isNew = !block;
        const isDataBlock = DATA_BLOCK_TYPES.includes(type);
        const canSave = !isDataBlock || fields.length > 0;

        return (
            <Modal isOpen={isOpen} toggle={toggle} className="block-editor-modal">
                <ModalHeader toggle={toggle}>
                    {isNew ? t('templates.block_editor_title_new') : t('templates.block_editor_title')}
                </ModalHeader>
                <ModalBody className="block-editor-modal-body">
                    <div className="block-editor-section">
                        <label className="block-editor-label">{t('templates.block_title_label')}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={title}
                            onChange={(e) => this.setState({title: e.target.value})}
                            placeholder={t('templates.block_type_' + type)}
                        />
                    </div>

                    <div className="block-editor-section">
                        <label className="block-editor-label">{t('templates.block_type_label')}</label>
                        <div className="block-editor-type-grid">
                            {BLOCK_TYPES.map(bt => (
                                <button
                                    key={bt}
                                    className={`block-editor-type-btn ${type === bt ? '-selected' : ''}`}
                                    onClick={() => this.handleTypeChange(bt)}
                                >
                                    <FaIcon iconName={BLOCK_TYPE_ICONS[bt]}/>
                                    <span>{t('templates.block_type_' + bt)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="block-editor-section">
                        <label className="block-editor-label">
                            {t('templates.block_col_span_label')}
                        </label>
                        <div className="block-editor-col-span-row">
                            <input
                                type="number"
                                className="form-control block-editor-col-span-input"
                                value={col_span}
                                onChange={(e) => this.setState({col_span: Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1))})}
                                min={1}
                                max={12}
                            />
                            <span className="block-editor-col-span-label">{t('templates.block_col_span_of')}</span>
                        </div>
                    </div>

                    {isDataBlock && (
                        <div className="block-editor-data-section">
                            <div className="block-editor-data-divider"/>

                            <div className="block-editor-section">
                                <label className="block-editor-label">{t('templates.block_fields_label')}</label>
                                <div className="block-editor-fields-chips">
                                    {fields.map((field, i) => (
                                        <span key={i} className="block-editor-field-chip">
                                            {field}
                                            <button
                                                className="block-editor-field-chip-remove"
                                                onClick={() => this.handleRemoveField(i)}
                                            >
                                                <FaIcon iconName="xmark"/>
                                            </button>
                                        </span>
                                    ))}
                                    <div className="block-editor-field-input-row">
                                        <input
                                            type="text"
                                            className="form-control block-editor-field-input"
                                            value={fieldInput}
                                            onChange={(e) => this.setState({fieldInput: e.target.value})}
                                            onKeyDown={this.handleFieldKeyDown}
                                            placeholder={t('templates.block_fields_add')}
                                        />
                                        <button
                                            className="btn -alt -small"
                                            onClick={this.handleAddField}
                                            disabled={!fieldInput.trim()}
                                        >
                                            <FaIcon iconName="plus"/>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="block-editor-section">
                                <label className="block-editor-label">{t('templates.block_date_range_label')}</label>
                                <select
                                    className="form-control"
                                    value={date_range_preset}
                                    onChange={(e) => this.setState({date_range_preset: e.target.value})}
                                >
                                    {DATE_RANGE_PRESETS.map(preset => (
                                        <option key={preset} value={preset}>
                                            {t('templates.date_range_' + preset)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="block-editor-section">
                                <label className="block-editor-label">{t('templates.block_comparison_label')}</label>
                                <select
                                    className="form-control"
                                    value={comparison_range_preset}
                                    onChange={(e) => this.setState({comparison_range_preset: e.target.value})}
                                >
                                    {COMPARISON_PRESETS.map(preset => (
                                        <option key={preset} value={preset}>
                                            {t('templates.comparison_' + preset)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="block-editor-section">
                                <label className="block-editor-label">{t('templates.block_data_source_label')}</label>
                                <select
                                    className="form-control"
                                    value={data_source_type}
                                    onChange={(e) => this.setState({data_source_type: e.target.value})}
                                >
                                    {DATA_SOURCE_TYPES.map(ds => (
                                        <option key={ds} value={ds}>
                                            {t('templates.block_data_source_' + ds.toLowerCase())}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="block-editor-modal-footer">
                    <button className="btn -negative" onClick={toggle}>
                        {t('client_reports.cancel')}
                    </button>
                    {!isNew && (
                        <button className="btn -negative -danger" onClick={this.handleDelete}>
                            <FaIcon iconName="trash"/>
                            {t('templates.block_delete')}
                        </button>
                    )}
                    <button
                        className="btn -alt"
                        onClick={this.handleSave}
                        disabled={!canSave}
                    >
                        {t('templates.block_save')}
                    </button>
                </ModalFooter>
            </Modal>
        );
    }
}

export const BlockEditorModal = withTranslation()(BlockEditorModalClass);
